/**
 * LayerSearch.tsx
 * Barre de recherche et filtres pour le panneau de calques (style Figma)
 * UX: Recherche rapide + filtres par type
 */
import React, { useState, useCallback } from 'react';
import type { ShapeType } from '../../../types/ShapeTypes';

interface LayerSearchProps {
  onSearchChange: (query: string) => void;
  onFilterChange: (filters: Set<ShapeType>) => void;
  searchQuery: string;
  activeFilters: Set<ShapeType>;
}

const LAYER_TYPES: { type: ShapeType; label: string; icon: string }[] = [
  { type: 'text', label: 'Texte', icon: '📝' },
  { type: 'image', label: 'Images', icon: '🖼️' },
  { type: 'photoSpace', label: 'Photos', icon: '📷' },
  { type: 'rect', label: 'Formes', icon: '▭' },
  { type: 'circle', label: 'Cercles', icon: '○' },
];

export const LayerSearch: React.FC<LayerSearchProps> = ({
  onSearchChange,
  onFilterChange,
  searchQuery,
  activeFilters,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFilterToggle = useCallback(
    (type: ShapeType) => {
      const newFilters = new Set(activeFilters);
      if (newFilters.has(type)) {
        newFilters.delete(type);
      } else {
        newFilters.add(type);
      }
      onFilterChange(newFilters);
    },
    [activeFilters, onFilterChange]
  );

  const hasActiveFilters = activeFilters.size > 0;

  return (
    <div className="layer-search-container">
      <div className="layer-search-input-wrapper">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="layer-search-icon"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="text"
          className="layer-search-input"
          placeholder="Rechercher un calque..."
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          onFocus={() => setIsExpanded(true)}
        />
        {searchQuery && (
          <button
            className="layer-search-clear"
            onClick={() => onSearchChange('')}
            aria-label="Effacer la recherche"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
        <button
          className={`layer-search-filter-toggle ${hasActiveFilters ? 'active' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
          title="Filtres"
          aria-label="Afficher les filtres"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          {hasActiveFilters && (
            <span className="layer-search-filter-badge">{activeFilters.size}</span>
          )}
        </button>
      </div>
      {isExpanded && (
        <div className="layer-search-filters">
          {LAYER_TYPES.map(({ type, label, icon }) => (
            <button
              key={type}
              className={`layer-search-filter ${activeFilters.has(type) ? 'active' : ''}`}
              onClick={() => handleFilterToggle(type)}
            >
              <span>{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
